import React from "react";
import { INCOME_BRACKETS } from "@/lib/netprice-brackets";

/**
 * BracketChart — horizontal bar chart of the 5 federal income brackets.
 * The user's selected bracket is highlighted. Bars are scaled relative to
 * the maximum bracket value so the chart adapts to each school.
 */
export default function BracketChart({ brackets, selectedKey }) {
  const max = Math.max(
    1,
    ...Object.values(brackets || {}).map((v) => (typeof v === "number" ? v : 0))
  );

  // The 5 federal brackets, in order. Q2 has 6 user-facing buckets,
  // but the top two ($110-150K and over $150K) both map to "over_110k".
  const FEDERAL_ROWS = [
    { key: "under_30k",  label: "Under $30K" },
    { key: "30k_48k",    label: "$30K-$48K" },
    { key: "48k_75k",    label: "$48K-$75K" },
    { key: "75k_110k",   label: "$75K-$110K" },
    { key: "over_110k",  label: "$110K and up" },
  ];

  // Allow either the federal key or the user's matching INCOME_BRACKETS apiKey
  const matched = INCOME_BRACKETS.find(b => b.label === selectedKey)?.apiKey || selectedKey;

  return (
    <div className="space-y-2.5" data-testid="netprice-bracket-chart">
      {FEDERAL_ROWS.map(row => {
        const v = brackets?.[row.key];
        const has = typeof v === "number";
        const pct = has ? Math.max(4, Math.round((v / max) * 100)) : 0;
        const isSelected = row.key === matched;
        return (
          <div key={row.key} className="grid grid-cols-[120px_1fr_90px] gap-3 items-center"
            data-testid={`netprice-row-${row.key}`}>
            <span className="text-xs sm:text-sm" style={{ color: isSelected ? "#1d4ed8" : "#6b7280", fontWeight: isSelected ? 500 : 400 }}>
              {row.label}{isSelected && " ←"}
            </span>
            <div className="h-7 rounded-md relative overflow-hidden" style={{ background: "#f3f4f6" }}>
              <div
                className="h-full rounded-md transition-all"
                style={{
                  width: `${pct}%`,
                  background: isSelected ? "#1d4ed8" : "#bfdbfe",
                }} />
            </div>
            <span className="text-xs sm:text-sm text-right tabular-nums"
              style={{ color: isSelected ? "#1d4ed8" : "#111928", fontWeight: isSelected ? 600 : 400 }}>
              {has ? `$${v.toLocaleString()}` : "n/a"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
